import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Menu, X } from "lucide-react";
import TooltipLogBlock from "@/tooltip/TooltipLogBlock";
import { LandingData } from "../core/buildLandingData";

interface HeaderLandingProps {
  className?: string;
  data?: LandingData | null;
}

const HeaderLanding: React.FC<HeaderLandingProps> = ({
  className = "text-sm md:text-xl font-semibold text-gray-900",
  data,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const displayName = data?.company?.name ?? "Profesional";
  const [open, setOpen] = useState(false);

  const from = location.state?.from || "/";
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        open &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const go = (path: string) => {
    if (!id) return;
    navigate(`/landing/${id}/${path}`);
  };

  return (
    <>
      <header className="fixed top-0 left-0 w-full z-40 bg-white/40 backdrop-blur-md shadow-md shadow-zinc-700">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-3">
          {/* IZQUIERDA */}
          <TooltipLogBlock
            label="Volver a la página principal"
            place="bottom"
            onClick={() => navigate(from)}
          >
            <h1 className="text-2xl font-bold text-gray-900 cursor-pointer">
              Link<span className="text-cyan-600">Servs</span>
            </h1>
          </TooltipLogBlock>

          {/* CENTRO */}
          <div className="hidden md:flex items-center gap-2">
            <span className="text-xl uppercase tracking-widest text-cyan-600">
              Landing
            </span>

            <span className="text-zinc-800">·</span>

            <button
              className={className}
              onClick={() => id && navigate(`/landing/${id}`)}
            >
              {displayName}
            </button>
          </div>

          {/* DERECHA */}
          <nav className="hidden md:flex gap-8 text-gray-700 font-medium">
            <button onClick={() => go("quienes-somos")}>Quienes Somos</button>

            <button onClick={() => go("ofertas")}>Ofertas</button>

            <button onClick={() => go("empleo")}>Empleo</button>

            <button onClick={() => go("contacto")}>Contacto</button>
          </nav>

          {/* HAMBURGUESA */}
          <button
            className="md:hidden text-gray-900"
            onClick={() => setOpen(true)}
          >
            <Menu size={28} />
          </button>
        </div>

        {/* MOBILE */}
        {open && (
          <>
            <div className="md:hidden bg-zinc-900/50 px-6 py-4 flex flex-col gap-4">
              {/* Overlay */}

              {/* Menú lateral */}
              <div
                ref={menuRef}
                className={`
                 fixed top-0 right-0 h-screen w-52
                 bg-zinc-900/60 backdrop-blur-xl
                 border-l border-white/10
                 shadow-2xl
                 z-50
                 transform transition-transform duration-300
                 ${open ? "translate-x-0" : "translate-x-full"}
               `}
              >
                <div className="flex items-center justify-between p-5 border-b border-white/10">
                  <h2 className="text-lg font-bold text-cyan-400">Menú</h2>

                  <button
                    onClick={() => setOpen(false)}
                    className="text-white hover:text-cyan-400 transition"
                  >
                    <X size={22} />
                  </button>
                </div>

                <nav className="flex flex-col py-4">
                  <button
                    onClick={() => {
                      id && navigate(`/landing/${id}`);
                      setOpen(false);
                    }}
                    className="text-left px-5 py-3 text-white/90 hover:bg-white/10 hover:text-cyan-300 transition rounded-lg mx-2"
                  >
                    🏠 Inicio
                  </button>

                  <button
                    onClick={() => {
                      go("quienes-somos");
                      setOpen(false);
                    }}
                    className="text-left px-5 py-3 text-white/90 hover:bg-white/10 hover:text-cyan-300 transition rounded-lg mx-2"
                  >
                    👥 Quienes Somos
                  </button>

                  <button
                    onClick={() => {
                      go("ofertas");
                      setOpen(false);
                    }}
                    className="text-left px-5 py-3 text-white/90 hover:bg-white/10 hover:text-cyan-300 transition rounded-lg mx-2"
                  >
                    🎁 Ofertas
                  </button>

                  <button
                    onClick={() => {
                      go("empleo");
                      setOpen(false);
                    }}
                    className="text-left px-5 py-3 text-white/90 hover:bg-white/10 hover:text-cyan-300 transition rounded-lg mx-2"
                  >
                    💼 Empleo
                  </button>

                  <button
                    onClick={() => {
                      go("contacto");
                      setOpen(false);
                    }}
                    className="text-left px-5 py-3 text-white/90 hover:bg-white/10 hover:text-cyan-300 transition rounded-lg mx-2"
                  >
                    ✉️ Contacto
                  </button>
                </nav>
              </div>
            </div>
          </>
        )}
      </header>
    </>
  );
};

export default HeaderLanding;
